import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { orgChartApi } from "./client";

export const orgChartKeys = {
  all: ["org-chart"] as const,
  overview: (params: { departmentId?: string; includeInactive?: boolean }) =>
    ["org-chart", "overview", params] as const,
  departments: (query: Partial<OffsetPageQueryDTO>) =>
    ["org-chart", "departments", query] as const,
  positions: (query: Partial<OffsetPageQueryDTO>) =>
    ["org-chart", "positions", query] as const,
};

export function useOrgChartOverview(
  params: { departmentId?: string; includeInactive?: boolean } = {},
) {
  return useQuery({
    queryKey: orgChartKeys.overview(params),
    queryFn: () => orgChartApi.getOverview(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useOrgChartDepartments(
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: orgChartKeys.departments(q),
    queryFn: () => orgChartApi.listDepartments(q),
  });
}

export function useOrgChartPositions(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: orgChartKeys.positions(q),
    queryFn: () => orgChartApi.listPositions(q),
  });
}

export function useUpdateNodesPosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (positions: Array<{ nodeId: string; x: number; y: number }>) =>
      orgChartApi.updateNodesPosition(positions),
    onSuccess: () => {
      // Invalidate all org-chart queries to refetch data
      queryClient.invalidateQueries({ queryKey: orgChartKeys.all });
      // Also refetch to ensure fresh data immediately
      queryClient.refetchQueries({ queryKey: orgChartKeys.all });
    },
    onError: () => {
      // Even on error, invalidate to prevent showing stale data
      // if the update partially succeeded on the server
      queryClient.invalidateQueries({ queryKey: orgChartKeys.all });
    },
    onSettled: () => {
      // Ensure cache is invalidated regardless of success/failure
      // This acts as a safety net
      queryClient.invalidateQueries({ queryKey: orgChartKeys.all });
    },
  });
}
